import MoneyRendererV3 from '../../../../../components/Money/MoneyRendererV3/MoneyRendererV3';
import Typography from '../../../../../components/Typography/Typography';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../translation/TranslationContext';
import { OrderProductGridItem } from '../../orderProducts.types';
import { useOrderProductStyles } from '../styles/useOrderProductStyles';

interface OrderProductStatusTooltipContentProps {
  grid: OrderProductGridItem[];
}

const OrderProductStatusTooltipContent = ({ grid }: OrderProductStatusTooltipContentProps) => {
  const { classes } = useOrderProductStyles();
  const { translate } = useTranslation();
  const {
    canceledItems,
    canceledItemsValue,
    pendingItems,
    pendingItemsValue,
    settledItems,
    settledItemsValue,
    reservedItems,
    reservedItemsValue,
  } = grid.reduce(
    (acc, gridItem) => ({
      pendingItems: acc.pendingItems + Number(gridItem.pendingQuantity || 0),
      settledItems: acc.settledItems + Number(gridItem.settledQuantity || 0),
      canceledItems: acc.canceledItems + Number(gridItem.canceledQuantity || 0),
      reservedItems: acc.reservedItems + Number(gridItem.reservedQuantity || 0),
      pendingItemsValue:
        acc.pendingItemsValue +
        Number(gridItem.pendingQuantity || 0) * gridItem.orderWholeSalePrice,
      settledItemsValue:
        acc.settledItemsValue +
        Number(gridItem.settledQuantity || 0) * gridItem.orderWholeSalePrice,
      canceledItemsValue:
        acc.canceledItemsValue +
        Number(gridItem.canceledQuantity || 0) * gridItem.orderWholeSalePrice,
      reservedItemsValue:
        acc.reservedItemsValue +
        Number(gridItem.reservedQuantity || 0) * gridItem.orderWholeSalePrice,
    }),
    {
      pendingItems: 0,
      settledItems: 0,
      canceledItems: 0,
      reservedItems: 0,
      pendingItemsValue: 0,
      settledItemsValue: 0,
      canceledItemsValue: 0,
      reservedItemsValue: 0,
    },
  );

  const tableHeader = (
    <tr>
      <th aria-label=" " />
      {grid.map((gridItem) => (
        <th align="left" key={gridItem.productSizeName}>
          <Typography variant="body1-medium">{gridItem.productSizeName}</Typography>
        </th>
      ))}
      <th aria-label=" " />
      <th aria-label=" " />
    </tr>
  );

  const orderRow = (
    <tr>
      <td align="left">
        <Typography variant="body1-medium">{translate(LangEnum.GLOBAL_ORDER)}</Typography>
      </td>
      {grid.map((gridItem) => (
        <td align="center" key={`O-${gridItem.productSizeName}`}>
          <Typography variant="body1-medium">{Number(gridItem.orderItemQuantity)}</Typography>
        </td>
      ))}
      <td align="center">
        <Typography variant="body1-medium">
          {pendingItems + settledItems + canceledItems + reservedItems}
        </Typography>
      </td>
      <td style={{ lineHeight: 1 }}>
        <MoneyRendererV3
          size="medium"
          value={pendingItemsValue + canceledItemsValue + settledItemsValue + reservedItemsValue}
        />
      </td>
    </tr>
  );

  const pendingRow = (
    <tr>
      <td align="left">
        <Typography variant="body1-medium">
          {translate(LangEnum.ORDER_ITEM_STATUS_PENDING)}
        </Typography>
      </td>
      {grid.map((gridItem) => (
        <td align="center" key={`P-${gridItem.productSizeName}`}>
          <Typography variant="body1-medium">
            {Number(gridItem.pendingQuantity) +
              Number(gridItem.settledQuantity) +
              Number(gridItem.reservedQuantity) +
              Number(gridItem.canceledQuantity) ===
            0
              ? Number(gridItem.orderItemQuantity)
              : Number(gridItem.pendingQuantity)}
          </Typography>
        </td>
      ))}
      <td align="center">
        <Typography variant="body1-medium">{pendingItems}</Typography>
      </td>
      <td style={{ lineHeight: 1 }}>
        <MoneyRendererV3 size="medium" value={pendingItemsValue} />
      </td>
    </tr>
  );

  const settledRow = (
    <tr>
      <td align="left">
        <Typography variant="body1-medium">
          {translate(LangEnum.ORDER_ITEM_STATUS_SETTLED)}
        </Typography>
      </td>
      {grid.map((gridItem) => (
        <td align="center" key={`S-${gridItem.productSizeName}`}>
          <Typography variant="body1-medium">{Number(gridItem.settledQuantity)}</Typography>
        </td>
      ))}
      <td align="center">
        <Typography variant="body1-medium">{settledItems}</Typography>
      </td>
      <td style={{ lineHeight: 1 }}>
        <MoneyRendererV3 size="medium" value={settledItemsValue} />
      </td>
    </tr>
  );

  const reservedRow = (
    <tr>
      <td align="left">
        <Typography variant="body1-medium">
          {translate(LangEnum.ORDER_ITEM_STATUS_RESERVED)}
        </Typography>
      </td>
      {grid.map((gridItem) => (
        <td align="center" key={`R-${gridItem.productSizeName}`}>
          <Typography variant="body1-medium">{Number(gridItem.reservedQuantity)}</Typography>
        </td>
      ))}
      <td align="center">
        <Typography variant="body1-medium">{reservedItems}</Typography>
      </td>
      <td style={{ lineHeight: 1 }}>
        <MoneyRendererV3 size="medium" value={reservedItemsValue} />
      </td>
    </tr>
  );

  const canceledRow = (
    <tr>
      <td align="left">
        <Typography variant="body1-medium">
          {translate(LangEnum.ORDER_ITEM_STATUS_CANCELED)}
        </Typography>
      </td>
      {grid.map((gridItem) => (
        <td align="center" key={`C-${gridItem.productSizeName}`}>
          <Typography variant="body1-medium">{Number(gridItem.canceledQuantity)}</Typography>
        </td>
      ))}
      <td align="center">
        <Typography variant="body1-medium">{canceledItems}</Typography>
      </td>
      <td style={{ lineHeight: 1 }}>
        <MoneyRendererV3 size="medium" value={canceledItemsValue} />
      </td>
    </tr>
  );

  return (
    <table className={classes.tooltipTablePadding}>
      <thead>{tableHeader}</thead>
      <tbody>
        {orderRow}
        {settledRow}
        {reservedRow}
        {pendingRow}
        {canceledRow}
      </tbody>
    </table>
  );
};

export default OrderProductStatusTooltipContent;
